import type { Route, RedirectOption } from 'vue-router';

/**
 * The billing surface runs on classic only.
 *
 * /subscription, /auto_subscription, /subscription/confirm and /onboard-pricing
 * were migrated to atlas (AS-10883..10886) and defaulted there (AS-10385). They
 * were taken back so the install funnel and every plan change behave exactly as
 * they did before the migration: no `subscription` feature in
 * config/atlasFeatures.ts, so no stored choice or "try the new version" banner
 * can move a merchant off these pages.
 *
 * The four atlas paths stay in the router as redirects back to classic, because
 * links to them are already out there: an open tab or bookmark, a menu or
 * onboarding link inside atlas that notifies the parent of its own path, and any
 * Shopify return or deep link issued while the atlas pages were live.
 */
export const ATLAS_BILLING_TO_CLASSIC: ReadonlyArray<readonly [string, string]> = [
  ['/assistant/subscription', '/subscription'],
  ['/assistant/auto-subscription', '/auto_subscription'],
  ['/assistant/subscription-confirm', '/subscription/confirm'],
  ['/assistant/onboard-pricing', '/onboard-pricing'],
];

/**
 * vue-router `redirect` for one of the paths above. The query rides along:
 * `charge_id` on the confirm hop is the difference between a paid plan
 * activating or not, and `discount` / `code` / `billing_package_id` / `interval`
 * pick what gets charged.
 */
export function classicBillingRedirect(classicPath: string): RedirectOption {
  return (to: Route) => ({ path: classicPath, query: to.query, hash: to.hash });
}

/** Route records for the router. Must be registered before `/assistant/:agent/:tab*`. */
export function atlasBillingRedirectRoutes(): Array<{ path: string; redirect: RedirectOption }> {
  return ATLAS_BILLING_TO_CLASSIC.map(([from, to]) => ({
    path: from,
    redirect: classicBillingRedirect(to),
  }));
}
